import { useEffect, useState } from "react";
import { HeartHandshake, ChevronRight, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · Compact preferences summary shown at the top of an arrival-prep
 * booking modal (team mode). Zieht die aktuellen Kunden-Präferenzen und
 * rendert sie einzeilig — Allergien werden immer sichtbar, weitere Kategorien
 * eingeklappt, sobald sie leer sind. Deep-Link führt zur Diff-Karte im
 * Kundendetail.
 */
export function BookingPrepPreferencesCard({ customerId }) {
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/team/customers/${customerId}/preferences`);
        if (!cancelled) setPrefs(data);
      } catch (e) {
        // Silent — Concierge muss trotzdem weiterarbeiten können.
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  const items = prefs || {};
  const hasAny =
    (items.allergies || []).length > 0 ||
    (items.food || []).length > 0 ||
    (items.home || []).length > 0 ||
    (items.notes || "").trim().length > 0;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-navy-dark/50 text-[12px] py-1">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Präferenzen laden …
      </div>
    );
  }

  return (
    <Link
      to={`/team/customers/${customerId}#preferences`}
      data-testid="booking-prep-preferences-card"
      className="block border p-3 md:p-4 hover:opacity-95 transition-opacity"
      style={{
        background: withAlpha(BRAND_COLORS.gold, 0.08),
        borderColor: BRAND_COLORS.gold,
      }}
      title="Zu den Präferenzen-Details im Kundenprofil springen"
    >
      <div className="flex items-start gap-3">
        <HeartHandshake
          className="w-4 h-4 mt-0.5 shrink-0"
          strokeWidth={1.8}
          style={{ color: BRAND_COLORS.gold }}
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div
            className="label-caps"
            style={{ color: BRAND_COLORS.goldDark || "#8A7635" }}
          >
            Präferenzen für diese Vorbereitung
          </div>
          {!hasAny ? (
            <div className="text-[12.5px] text-navy-dark/60 italic" data-testid="booking-prep-preferences-empty">
              Kunde hat noch keine Präferenzen hinterlegt.
            </div>
          ) : (
            <div className="space-y-1">
              <SummaryLine label="Allergien" tags={items.allergies} testid="booking-prep-allergies" />
              <SummaryLine label="Ernährung" tags={items.food} testid="booking-prep-food" />
              <SummaryLine label="Zuhause" tags={items.home} testid="booking-prep-home" />
              {(items.notes || "").trim() && (
                <div className="text-[12px] text-navy-dark/85 italic leading-snug" data-testid="booking-prep-notes">
                  „{items.notes}"
                </div>
              )}
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 shrink-0 mt-0.5" style={{ color: BRAND_COLORS.gold }} />
      </div>
    </Link>
  );
}

function SummaryLine({ label, tags, testid }) {
  if (!tags || tags.length === 0) return null;
  return (
    <div className="text-[12.5px] text-navy-dark leading-snug" data-testid={testid}>
      <span
        className="label-caps mr-2"
        style={{ color: BRAND_COLORS.goldDark || "#8A7635" }}
      >
        {label}
      </span>
      <span>{tags.join(" · ")}</span>
    </div>
  );
}
